import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const MarkdownMessage = ({ content, isUser = false }) => {
  if (!content) return null;

  if (isUser) {
    return (
      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words text-white">
        {content}
      </div>
    );
  }

  return (
    <div className="markdown-content text-sm leading-relaxed text-slate-850 dark:text-slate-100 break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...props }) => (
            <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white mt-4 mb-2 first:mt-0 tracking-tight" {...props} />
          ),
          h2: ({ node, ...props }) => (
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white mt-3.5 mb-2 first:mt-0 tracking-tight flex items-center gap-1.5" {...props} />
          ),
          h3: ({ node, ...props }) => (
            <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white mt-3 mb-1.5 first:mt-0 tracking-tight" {...props} />
          ),
          p: ({ node, ...props }) => (
            <p className="mb-2.5 last:mb-0 leading-relaxed text-slate-800 dark:text-slate-200" {...props} />
          ),
          strong: ({ node, ...props }) => (
            <strong className="font-bold text-slate-900 dark:text-white" {...props} />
          ),
          b: ({ node, ...props }) => (
            <b className="font-bold text-slate-900 dark:text-white" {...props} />
          ),
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 my-2.5 space-y-1.5 text-slate-800 dark:text-slate-200 marker:text-blue-900 dark:marker:text-cyan-400" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 my-2.5 space-y-1.5 text-slate-800 dark:text-slate-200 marker:font-bold marker:text-blue-900 dark:marker:text-cyan-400" {...props} />
          ),
          li: ({ node, ...props }) => (
            <li className="pl-1 leading-relaxed" {...props} />
          ),
          hr: ({ node, ...props }) => (
            <hr className="my-3.5 border-t border-slate-200 dark:border-white/10" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-3 border-blue-900 dark:border-cyan-400 pl-3 py-1 my-2.5 italic text-slate-700 dark:text-slate-300 bg-slate-200/50 dark:bg-white/5 rounded-r" {...props} />
          ),
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <pre className="p-3 my-2.5 rounded-xl bg-slate-900 text-slate-100 text-xs font-mono overflow-x-auto border border-white/10 shadow-sm">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code className="px-1.5 py-0.5 rounded text-[13px] font-mono bg-slate-200/70 dark:bg-slate-900/80 text-blue-950 dark:text-cyan-300 border border-slate-300/40 dark:border-white/10" {...props}>
                {children}
              </code>
            );
          },
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-3.5 rounded-xl border border-blue-900/30 dark:border-blue-700/50 shadow-sm overflow-hidden bg-transparent">
              <table className="min-w-full text-xs text-left border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead className="bg-blue-900 dark:bg-blue-950 font-bold border-b border-blue-900/40 dark:border-blue-800 text-white" {...props} />
          ),
          th: ({ node, ...props }) => (
            <th className="px-3.5 py-2.5 font-bold text-white text-xs tracking-wide border-r border-white/20 dark:border-white/10 last:border-r-0" {...props} />
          ),
          tbody: ({ node, ...props }) => (
            <tbody className="divide-y divide-blue-900/20 dark:divide-blue-700/30 bg-transparent text-slate-800 dark:text-slate-200" {...props} />
          ),
          tr: ({ node, ...props }) => (
            <tr className="hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-3.5 py-2.5 text-xs text-slate-800 dark:text-slate-200 border-r border-blue-900/20 dark:border-blue-700/30 last:border-r-0" {...props} />
          ),
          a: ({ node, ...props }) => (
            <a className="text-blue-700 dark:text-cyan-400 underline font-medium hover:text-blue-900 dark:hover:text-cyan-300 transition-colors" target="_blank" rel="noopener noreferrer" {...props} />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
